import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerVenueLocation, uploadVenueImages, searchVenues } from '../utils/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, AlertCircle, Camera, X } from 'lucide-react';

// Fix Leaflet's broken default marker icons when bundled with webpack/CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});


export default function RegisterVenueLocation() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [form, setForm] = useState({
    fullName: '',
    mobile: '',
    venueCode: '',
    venueName: '',
    latitude: '',
    longitude: '',
    address: ''
  });

  const [capturedImages, setCapturedImages] = useState([]); // File objects from camera
  const [imagePreviews, setImagePreviews] = useState([]);   // data URLs for preview
  const [cameraOpen, setCameraOpen] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState('');
  const [geoError, setGeoError] = useState('');
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [showVenueDropdown, setShowVenueDropdown] = useState(false);
  const [venueSearchLoading, setVenueSearchLoading] = useState(false);
  const venueSearchTimer = useRef(null);

  // Initialize Leaflet Map
  const initializeMap = () => {
    // Prevent duplicate map initialization
    if (mapInstanceRef.current) return;
    
    if (!mapRef.current) return;

    try {
      // Create map centered on IIT Delhi
      mapInstanceRef.current = L.map(mapRef.current).setView([28.5494, 77.1668], 15);

      // Add OpenStreetMap tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(mapInstanceRef.current);
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };

  // Update map marker
  const updateMapMarker = (lat, lng) => {
    if (!mapInstanceRef.current) {
      initializeMap();
    }

    const location = L.latLng(parseFloat(lat), parseFloat(lng));

    // Remove old marker
    if (markerRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
    }

    // Add new marker
    markerRef.current = L.marker(location)
      .bindPopup(form.venueName || 'Venue Location')
      .addTo(mapInstanceRef.current);

    // Center map on marker
    mapInstanceRef.current.setView(location, 15);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors([]);

    // Update map if coordinates change
    if (name === 'latitude' || name === 'longitude') {
      if (form.latitude && form.longitude) {
        updateMapMarker(
          name === 'latitude' ? value : form.latitude,
          name === 'longitude' ? value : form.longitude
        );
      }
    }
  };

  const handleVenueSearch = (searchTerm) => {
    setForm(prev => ({ ...prev, venueCode: searchTerm }));

    // Clear previous timer
    if (venueSearchTimer.current) {
      clearTimeout(venueSearchTimer.current);
    }

    if (searchTerm.trim() === '') {
      setFilteredVenues([]);
      setShowVenueDropdown(false);
      return;
    }

    // Debounce API call by 300ms
    setShowVenueDropdown(true);
    venueSearchTimer.current = setTimeout(async () => {
      setVenueSearchLoading(true);
      try {
        const result = await searchVenues(searchTerm.trim());
        const venues = (result.data || []).map(v => ({
          venueCode: v.venue_code,
          venueName: v.name,
          location: v['venue-detail']?.location || v['venue-detail']?.city_details?.name || ''
        }));
        setFilteredVenues(venues);
      } catch (err) {
        console.error('Venue search failed:', err);
        setFilteredVenues([]);
      } finally {
        setVenueSearchLoading(false);
      }
    }, 300);
  };

  const handleSelectVenue = (venue) => {
    setForm(prev => ({
      ...prev,
      venueCode: venue.venueCode,
      venueName: venue.venueName
    }));
    setShowVenueDropdown(false);
  };

  const handleFetchLocation = () => {
    setGeoError('');

    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser');
      return;
    }

    setFetchingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        setForm(prev => ({
          ...prev,
          latitude: latitude.toFixed(6),
          longitude: longitude.toFixed(6)
        }));

        updateMapMarker(latitude.toFixed(6), longitude.toFixed(6));

        setGeoError('');
        setFetchingLocation(false);
      },
      (error) => {
        let errorMessage = 'Failed to get your location';

        if (error.code === error.PERMISSION_DENIED) {
          errorMessage = 'Location permission denied. Please enable geolocation in your browser settings.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = 'Location information is unavailable.';
        } else if (error.code === error.TIMEOUT) {
          errorMessage = 'Location request timed out.';
        }

        setGeoError(errorMessage);
        setFetchingLocation(false);
      },
      {
        timeout: 10000,
        enableHighAccuracy: true
      }
    );
  };

  const validateForm = () => {
    const newErrors = [];

    if (!form.fullName || form.fullName.length < 2) {
      newErrors.push('Full name must be at least 2 characters');
    }

    if (!form.mobile || !/^\d{10}$/.test(form.mobile)) {
      newErrors.push('Mobile number must be exactly 10 digits');
    }

    if (!form.venueCode) {
      newErrors.push('Please select a venue');
    }

    if (!form.latitude || isNaN(form.latitude)) {
      newErrors.push('Latitude is required and must be a valid number');
    } else if (form.latitude < -90 || form.latitude > 90) {
      newErrors.push('Latitude must be between -90 and 90');
    }

    if (!form.longitude || isNaN(form.longitude)) {
      newErrors.push('Longitude is required and must be a valid number');
    } else if (form.longitude < -180 || form.longitude > 180) {
      newErrors.push('Longitude must be between -180 and 180');
    }

    return newErrors;
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRef.current = stream;
      setCameraOpen(true);
      // Attach stream to video element after modal renders
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch {
      setErrors(['Camera access denied. Please allow camera permission and try again.']);
    }
  };

  const closeCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setImagePreviews((prev) => [...prev, dataUrl]);
    // Convert dataUrl to File for upload
    canvas.toBlob((blob) => {
      const file = new File([blob], `venue-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setCapturedImages((prev) => [...prev, file]);
    }, 'image/jpeg', 0.85);
  };

  const removeImage = (index) => {
    setCapturedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSuccess('');

    // Validate
    const newErrors = validateForm();
    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      // Upload images first if any captured
      let imageUrls = [];
      if (capturedImages.length > 0) {
        setUploadingImages(true);
        const uploadResult = await uploadVenueImages(capturedImages);
        imageUrls = uploadResult.urls || [];
        setUploadingImages(false);
      }

      const payload = {
        fullName: form.fullName,
        mobile: form.mobile,
        venueCode: form.venueCode,
        venueName: form.venueName || 'Unknown Venue',
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        address: form.address || null,
        imageUrls
      };

      await registerVenueLocation(payload);

      setSuccess('Venue location registered successfully!');
      setForm({
        fullName: '',
        mobile: '',
        venueCode: '',
        venueName: '',
        latitude: '',
        longitude: '',
        address: ''
      });

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/venue-location-change-requests');
      }, 2000);
    } catch (error) {
      console.error('Registration error:', error);
      setErrors([error.message || 'Failed to register venue location']);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    // Initialize Leaflet map
    initializeMap();

    // Cleanup on component unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold">Register Venue Location</h2>
            <p className="text-sm text-gray-600 mt-1">Register a new venue location with GPS coordinates</p>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 font-medium">{success}</p>
            <p className="text-sm text-green-700 mt-1">Redirecting to your requests...</p>
          </div>
        )}

        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-medium mb-2">Validation Errors</h3>
            <ul className="list-disc list-inside space-y-1">
              {errors.map((error, idx) => (
                <li key={idx} className="text-red-700 text-sm">{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Geolocation Error */}
        {geoError && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex gap-3 items-start">
              <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-yellow-800 font-medium">{geoError}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Venue Selection */}
              <div className="relative">
                <label className="text-sm font-medium text-gray-700">Select Venue *</label>
                <div className="relative mt-1">
                  <input
                    type="text"
                    value={form.venueCode}
                    onChange={(e) => handleVenueSearch(e.target.value)}
                    onFocus={() => setShowVenueDropdown(true)}
                    placeholder="Search by venue code or name"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {showVenueDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                      {venueSearchLoading ? (
                        <div className="p-3 text-center text-gray-500">Searching...</div>
                      ) : filteredVenues.length > 0 ? (
                        filteredVenues.map((venue) => (
                          <div
                            key={venue.venueCode}
                            onClick={() => handleSelectVenue(venue)}
                            className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{venue.venueName}</div>
                            <div className="text-xs text-gray-600">{venue.venueCode}{venue.location ? ` - ${venue.location}` : ''}</div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-gray-400">No venues found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Venue Display */}
              {form.venueName && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-900">
                    <span className="font-medium">Selected Venue:</span> {form.venueName} ({form.venueCode})
                  </p>
                </div>
              )}

              {/* Full Name */}
              <div>
                <label className="text-sm font-medium text-gray-700">Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Mobile Number */}
              <div>
                <label className="text-sm font-medium text-gray-700">Mobile Number *</label>
                <input
                  type="tel"
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  maxLength="10"
                  pattern="\d{10}"
                  required
                  className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Format: 10 digits (e.g., 9876543210)</p>
              </div>

              {/* Location Section */}
              <div className="pt-4 border-t">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Location Details</h3>

                {/* Fetch Location Button */}
                <button
                  type="button"
                  id="fetchLocationBtn"
                  onClick={handleFetchLocation}
                  disabled={fetchingLocation}
                  className="w-full mb-4 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {fetchingLocation ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Fetching Location...
                    </>
                  ) : (
                    <>
                      <MapPin size={20} />
                      Fetch My Location
                    </>
                  )}
                </button>

                {/* Latitude & Longitude */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Latitude *</label>
                    <input
                      type="number"
                      name="latitude"
                      value={form.latitude}
                      onChange={handleChange}
                      placeholder="Read-only"
                      readOnly
                      step="0.000001"
                      className="mt-1 w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Longitude *</label>
                    <input
                      type="number"
                      name="longitude"
                      value={form.longitude}
                      onChange={handleChange}
                      placeholder="Read-only"
                      readOnly
                      step="0.000001"
                      className="mt-1 w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Address (Optional) */}
                <div className="mt-4">
                  <label className="text-sm font-medium text-gray-700">Address (Optional)</label>
                  <textarea
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Enter address details"
                    rows="3"
                    className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Images Section */}
              <div className="pt-4 border-t">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Venue Images (Optional)</h3>

                <button
                  type="button"
                  onClick={openCamera}
                  disabled={capturedImages.length >= 10}
                  className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:bg-blue-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera size={20} className="text-gray-500" />
                  {capturedImages.length === 0 ? 'Open Camera to Capture Image' : `Capture Another Image (${capturedImages.length}/10)`}
                </button>

                {/* Captured previews */}
                {imagePreviews.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {imagePreviews.map((src, i) => (
                      <div key={i} className="relative group">
                        <img src={src} alt={`capture-${i}`} className="w-full h-24 object-cover rounded-lg border border-gray-200" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {uploadingImages && (
                  <p className="text-sm text-blue-600 mt-2 flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Uploading images...
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4 border-t">
                <button
                  type="submit"
                  disabled={loading || uploadingImages}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:bg-gray-400"
                >
                  {uploadingImages ? 'Uploading images...' : loading ? 'Registering...' : 'Submit Registration'}
                </button>
              </div>
            </form>
          </div>

          {/* Map */}
          <div className="lg:col-span-1 bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Venue Location Map</h3>
            <div
              ref={mapRef}
              className="w-full rounded-lg border border-gray-300"
              style={{ minHeight: '300px' }}
            ></div>
            {form.latitude && form.longitude && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                <p><strong>Coordinates:</strong></p>
                <p>Lat: {form.latitude}</p>
                <p>Long: {form.longitude}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Camera Modal */}
      {cameraOpen && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full max-w-lg rounded-lg"
          />
          <canvas ref={canvasRef} className="hidden" />
          <div className="flex gap-4 mt-6">
            <button
              type="button"
              onClick={capturePhoto}
              className="px-6 py-3 bg-white text-black font-semibold rounded-full flex items-center gap-2 hover:bg-gray-100 transition"
            >
              <Camera size={20} />
              Capture
            </button>
            <button
              type="button"
              onClick={closeCamera}
              className="px-6 py-3 bg-red-600 text-white font-semibold rounded-full flex items-center gap-2 hover:bg-red-700 transition"
            >
              <X size={20} />
              Done
            </button>
          </div>
          <p className="text-gray-400 text-sm mt-3">{imagePreviews.length} photo{imagePreviews.length !== 1 ? 's' : ''} captured</p>
        </div>
      )}
    </div>
  );
}
